// SPDX-License-Identifier: MIT
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

type Navigate = (path: string, opts?: { replace?: boolean }) => void;

function readHashError(hash: string): string | null {
  if (!hash) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return params.get('error') ?? params.get('error_description');
}

// Implicit-flow magic-link redirects land on /auth/callback with the access
// token in the URL hash (#access_token=...&refresh_token=...). supabase-js
// parses that hash inside its async _initialize() routine when the client
// is constructed with detectSessionInUrl:true + flowType:'implicit'. Until
// _initialize() resolves, getSession() returns null even though the hash is
// valid — which made the v1.0.4 callback handler always fall through to
// /?auth=expired despite a successful server-side /verify 303 + login event
// (caught empirically prod 2026-04-29 via GoTrue logs : action=login on
// /verify followed by /user 401 no_authorization).
//
// Subscribing to onAuthStateChange BEFORE checking session catches the
// INITIAL_SESSION (or SIGNED_IN) event that fires once detectSessionInUrl
// finishes parsing the hash. The 3s timeout is a safety net for the
// already-initialized case (e.g. cached session from a prior tab) where
// the event has already fired before we subscribed — getSession() returns
// the stored session in that case.
async function waitForInitialSession(timeoutMs = 3000): Promise<Session | null> {
  return new Promise((resolve) => {
    let settled = false;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (settled) return;
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
        settled = true;
        clearTimeout(t);
        subscription.unsubscribe();
        resolve(session ?? null);
      }
    });
    const t = setTimeout(async () => {
      if (settled) return;
      settled = true;
      subscription.unsubscribe();
      const { data } = await supabase.auth.getSession();
      resolve(data.session ?? null);
    }, timeoutMs);
  });
}

export async function handleCallback(navigate: Navigate): Promise<void> {
  const url = new URL(window.location.href);
  const errQuery = url.searchParams.get('error') ?? url.searchParams.get('error_description');
  const errHash = readHashError(url.hash);
  if (errQuery || errHash) {
    navigate('/?auth=expired', { replace: true });
    return;
  }

  const code = url.searchParams.get('code');
  try {
    if (code) {
      // PKCE flow path : explicit code-for-session exchange.
      const { error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) throw error;
      const { data } = await supabase.auth.getSession();
      navigate(data.session ? '/welcome' : '/?auth=expired', { replace: true });
      return;
    }

    // Implicit flow path : await the INITIAL_SESSION event so we don't race
    // detectSessionInUrl. This is what v1.0.4 should have included alongside
    // the explicit flowType:'implicit' fix — the flag tells supabase-js to
    // look at the hash, but the read still happens asynchronously.
    const session = await waitForInitialSession();
    navigate(session ? '/welcome' : '/?auth=expired', { replace: true });
  } catch (err) {
    console.warn('[auth] callback error:', err);
    navigate('/?auth=expired', { replace: true });
  }
}
